/** @flow */

// Forked from https://github.com/bvaughn/react-virtualized/commit/2ad62f15aee6cc8918e56b197e2f9a8f074e921d
//
// Changes:
// - Added onRender property
// - Added cellRangeRenderer property

import * as PropTypes from 'prop-types';
import * as React from 'react';
import {polyfill} from 'react-lifecycles-compat';
import {Grid, GridCellRangeRenderer, CellMeasurerCache} from 'react-virtualized';
import {MultiGridCellRenderer, ScrollbarPresenceChange, MultiGridCellProps} from './types';
import {Shadow9} from '../../../styles/colors';
import CellMeasurerCacheDecorator from './CellMeasurerCacheDecorator';
import {CellMeasurerCacheInterface} from 'react-virtualized/dist/es/CellMeasurer';

const SCROLLBAR_SIZE_BUFFER = 20;

interface State {
    scrollLeft?: number;
    scrollTop?: number;
    scrollbarSize?: number;
    showHorizontalScrollbar?: boolean;
    showVerticalScrollbar?: boolean;
}

interface ScrollPosition {
    scrollTop?: number;
    scrollLeft?: number;
}

interface Props {
    fixedColumnCount: number;
    fixedRowCount: number;
    scrollLeft?: number;
    scrollTop?: number;
    onScroll?: (pos: ScrollPosition) => void;
    onSectionRendered?: () => void;
    onScrollbarPresenceChange?: (param: ScrollbarPresenceChange) => void;
    scrollToColumn?: number;
    scrollToRow?: number;
    width: number;
    height: number;
    cellRenderer?: MultiGridCellRenderer;
    columnCount?: number;
    rowCount: number;
    columnWidth?: number | (({index}: {index: number}) => number);
    rowHeight: number | (({index}: {index: number}) => number);
    enableFixedColumnScroll?: boolean;
    enableFixedRowScroll?: boolean;
    style?: React.CSSProperties;
    styleBottomLeftGrid?: React.CSSProperties;
    styleBottomRightGrid?: React.CSSProperties;
    styleTopLeftGrid?: React.CSSProperties;
    styleTopRightGrid?: React.CSSProperties;
    classNameBottomLeftGrid?: React.CSSProperties;
    classNameBottomRightGrid?: React.CSSProperties;
    classNameTopLeftGrid?: React.CSSProperties;
    classNameTopRightGrid?: React.CSSProperties;
    hideTopRightGridScrollbar?: boolean;
    hideBottomLeftGridScrollbar?: boolean;
    onRender?: () => void;
    cellRangeRenderer?: GridCellRangeRenderer;
    fixedColumnsWidth: () => number;
    deferredMeasurementCache?: CellMeasurerCache;
}

/**
 * Renders 1, 2, or 4 Grids depending on configuration.
 * A main (body) Grid will always be rendered.
 * Optionally, 1-2 Grids for sticky header rows will also be rendered.
 * If no sticky columns, only 1 sticky header Grid will be rendered.
 * If sticky columns, 2 sticky header Grids will be rendered.
 */
class MultiGrid extends React.PureComponent<Props, State> {
    public static propTypes = {
        classNameBottomLeftGrid: PropTypes.string.isRequired,
        classNameBottomRightGrid: PropTypes.string.isRequired,
        classNameTopLeftGrid: PropTypes.string.isRequired,
        classNameTopRightGrid: PropTypes.string.isRequired,
        enableFixedColumnScroll: PropTypes.bool.isRequired,
        enableFixedRowScroll: PropTypes.bool.isRequired,
        fixedColumnCount: PropTypes.number.isRequired,
        fixedRowCount: PropTypes.number.isRequired,
        onScrollbarPresenceChange: PropTypes.func,
        style: PropTypes.object.isRequired,
        styleBottomLeftGrid: PropTypes.object.isRequired,
        styleBottomRightGrid: PropTypes.object.isRequired,
        styleTopLeftGrid: PropTypes.object.isRequired,
        styleTopRightGrid: PropTypes.object.isRequired,
        hideTopRightGridScrollbar: PropTypes.bool,
        hideBottomLeftGridScrollbar: PropTypes.bool,
    };

    public static defaultProps = {
        classNameBottomLeftGrid: '',
        classNameBottomRightGrid: '',
        classNameTopLeftGrid: '',
        classNameTopRightGrid: '',
        enableFixedColumnScroll: false,
        enableFixedRowScroll: false,
        fixedColumnCount: 0,
        fixedRowCount: 0,
        scrollToColumn: -1,
        scrollToRow: -1,
        style: {},
        styleBottomLeftGrid: {},
        styleBottomRightGrid: {},
        styleTopLeftGrid: {},
        styleTopRightGrid: {},
        hideTopRightGridScrollbar: false,
        hideBottomLeftGridScrollbar: false,
    };

    public state = {
        scrollLeft: 0,
        scrollTop: 0,
        scrollbarSize: 0,
        showHorizontalScrollbar: false,
        showVerticalScrollbar: false,
    };

    private _deferredInvalidateColumnIndex?: number;
    private _deferredInvalidateRowIndex?: number;

    private _deferredMeasurementCacheBottomLeftGrid?: CellMeasurerCacheInterface;
    private _deferredMeasurementCacheBottomRightGrid?: CellMeasurerCacheInterface;
    private _deferredMeasurementCacheTopRightGrid?: CellMeasurerCacheInterface;

    private _bottomLeftGrid?: Grid;
    private _bottomRightGrid?: Grid;
    private _topLeftGrid?: Grid;
    private _topRightGrid?: Grid;

    private _leftGridWidth?: number;
    private _topGridHeight?: number;

    private _containerOuterStyle?: React.CSSProperties;
    private _containerTopStyle?: React.CSSProperties;
    private _containerBottomStyle?: React.CSSProperties;

    private _bottomLeftGridStyle?: React.CSSProperties;
    private _bottomRightGridStyle?: React.CSSProperties;
    private _topLeftGridStyle?: React.CSSProperties;
    private _topRightGridStyle?: React.CSSProperties;

    private _lastRenderedColumnWidth?: number;
    private _lastRenderedFixedColumnCount?: number;
    private _lastRenderedFixedRowCount?: number;
    private _lastRenderedWidth?: number;
    private _lastRenderedHeight?: number;
    private _lastRenderedRowHeight?: number;
    private _lastRenderedStyle?: React.CSSProperties;
    private _lastRenderedStyleBottomLeftGrid?: React.CSSProperties;
    private _lastRenderedStyleBottomRightGrid?: React.CSSProperties;
    private _lastRenderedStyleTopLeftGrid?: React.CSSProperties;
    private _lastRenderedStyleTopRightGrid?: React.CSSProperties;

    constructor(props: Props, context) {
        super(props, context);

        const {deferredMeasurementCache, fixedColumnCount, fixedRowCount} = props;

        this._maybeCalculateCachedStyles(true);

        if (deferredMeasurementCache) {
            this._deferredMeasurementCacheBottomLeftGrid =
                fixedRowCount > 0
                    ? new CellMeasurerCacheDecorator({
                          cellMeasurerCache: deferredMeasurementCache,
                          columnIndexOffset: 0,
                          rowIndexOffset: fixedRowCount,
                      })
                    : deferredMeasurementCache;

            this._deferredMeasurementCacheBottomRightGrid =
                fixedColumnCount > 0 || fixedRowCount > 0
                    ? new CellMeasurerCacheDecorator({
                          cellMeasurerCache: deferredMeasurementCache,
                          columnIndexOffset: fixedColumnCount,
                          rowIndexOffset: fixedRowCount,
                      })
                    : deferredMeasurementCache;

            this._deferredMeasurementCacheTopRightGrid =
                fixedColumnCount > 0
                    ? new CellMeasurerCacheDecorator({
                          cellMeasurerCache: deferredMeasurementCache,
                          columnIndexOffset: fixedColumnCount,
                          rowIndexOffset: 0,
                      })
                    : deferredMeasurementCache;
        }
    }

    public forceUpdateGrids() {
        this._bottomLeftGrid && this._bottomLeftGrid.forceUpdate();
        this._bottomRightGrid && this._bottomRightGrid.forceUpdate();
        this._topLeftGrid && this._topLeftGrid.forceUpdate();
        this._topRightGrid && this._topRightGrid.forceUpdate();
    }

    /** See Grid#invalidateCellSizeAfterRender */
    public invalidateCellSizeAfterRender({columnIndex = 0, rowIndex = 0} = {}) {
        this._deferredInvalidateColumnIndex =
            typeof this._deferredInvalidateColumnIndex === 'number' ? Math.min(this._deferredInvalidateColumnIndex, columnIndex) : columnIndex;
        this._deferredInvalidateRowIndex =
            typeof this._deferredInvalidateRowIndex === 'number' ? Math.min(this._deferredInvalidateRowIndex, rowIndex) : rowIndex;
    }

    /** See Grid#measureAllCells */
    public measureAllCells() {
        this._bottomLeftGrid && this._bottomLeftGrid.measureAllCells();
        this._bottomRightGrid && this._bottomRightGrid.measureAllCells();
        this._topLeftGrid && this._topLeftGrid.measureAllCells();
        this._topRightGrid && this._topRightGrid.measureAllCells();
    }

    /** See Grid#recomputeGridSize */
    public recomputeGridSize = ({columnIndex = 0, rowIndex = 0} = {}) => {
        const {fixedColumnCount = 0, fixedRowCount = 0} = this.props;

        const adjustedColumnIndex = Math.max(0, columnIndex - fixedColumnCount);
        const adjustedRowIndex = Math.max(0, rowIndex - fixedRowCount);

        this._bottomLeftGrid &&
            this._bottomLeftGrid.recomputeGridSize({
                columnIndex,
                rowIndex: adjustedRowIndex,
            });
        this._bottomRightGrid &&
            this._bottomRightGrid.recomputeGridSize({
                columnIndex: adjustedColumnIndex,
                rowIndex: adjustedRowIndex,
            });
        this._topLeftGrid &&
            this._topLeftGrid.recomputeGridSize({
                columnIndex,
                rowIndex,
            });
        this._topRightGrid &&
            this._topRightGrid.recomputeGridSize({
                columnIndex: adjustedColumnIndex,
                rowIndex,
            });

        this._leftGridWidth = undefined;
        this._topGridHeight = undefined;
        this._maybeCalculateCachedStyles(true);
    };

    public static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.scrollLeft !== prevState.scrollLeft || nextProps.scrollTop !== prevState.scrollTop) {
            return {
                scrollLeft: nextProps.scrollLeft != null && nextProps.scrollLeft >= 0 ? nextProps.scrollLeft : prevState.scrollLeft,
                scrollTop: nextProps.scrollTop != null && nextProps.scrollTop >= 0 ? nextProps.scrollTop : prevState.scrollTop,
            };
        }

        return null;
    }

    public componentDidMount() {
        const {scrollLeft = 0, scrollTop = 0} = this.props;

        if (scrollLeft > 0 || scrollTop > 0) {
            const newState: State = {};

            if (scrollLeft > 0) {
                newState.scrollLeft = scrollLeft;
            }

            if (scrollTop > 0) {
                newState.scrollTop = scrollTop;
            }

            this.setState(newState);
        }
        this._handleInvalidatedGridSize();
    }

    public componentDidUpdate() {
        this._handleInvalidatedGridSize();
    }

    public render() {
        const {
            onScroll,
            onSectionRendered,
            onScrollbarPresenceChange, // eslint-disable-line no-unused-vars
            scrollLeft: scrollLeftProp, // eslint-disable-line no-unused-vars
            scrollToColumn,
            scrollTop: scrollTopProp, // eslint-disable-line no-unused-vars
            scrollToRow,
            fixedColumnsWidth,
            ...rest
        } = this.props;

        this._prepareForRender();

        // Don't render any of our Grids if there are no cells.
        // This mirrors what Grid does,
        // And prevents us from recording inaccurage measurements when used with CellMeasurer.
        if (this.props.width === 0 || this.props.height === 0) {
            return null;
        }

        if (this.props.onRender) {
            this.props.onRender();
        }

        // scrollTop and scrollLeft props are explicitly filtered out and ignored
        const {scrollLeft, scrollTop} = this.state;

        return (
            <div style={this._containerOuterStyle}>
                <div style={this._containerTopStyle}>
                    {this._renderTopLeftGrid(rest)}
                    {this._renderTopRightGrid({
                        ...rest,
                        onScroll,
                        scrollLeft,
                    })}
                </div>
                <div style={this._containerBottomStyle}>
                    {this._renderBottomLeftGrid({
                        ...rest,
                        onScroll,
                        scrollTop,
                    })}
                    {this._renderBottomRightGrid({
                        ...rest,
                        onScroll,
                        onSectionRendered,
                        scrollLeft,
                        scrollToColumn,
                        scrollToRow,
                        scrollTop,
                    })}
                </div>
                <div
                    style={{
                        display: scrollLeft ? 'block' : 'none',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: fixedColumnsWidth() + 10,
                        bottom: SCROLLBAR_SIZE_BUFFER,
                        backgroundColor: 'transparent',
                        pointerEvents: 'none',
                        overflow: 'hidden',
                    }}>
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            right: 10,
                            bottom: 0,
                            backgroundColor: 'transparent',
                            boxShadow: Shadow9,
                            pointerEvents: 'none',
                        }}
                    />
                </div>
            </div>
        );
    }

    private _bottomLeftGridRef = (ref) => {
        this._bottomLeftGrid = ref;
    };

    private _bottomRightGridRef = (ref) => {
        this._bottomRightGrid = ref;
    };

    private _cellRendererBottomLeftGrid = ({rowIndex, ...rest}: MultiGridCellProps) => {
        const {cellRenderer, fixedRowCount = 0, rowCount = 0} = this.props;

        if (rowIndex === rowCount - fixedRowCount) {
            return (
                <div
                    key={rest.key}
                    style={{
                        ...rest.style,
                        height: SCROLLBAR_SIZE_BUFFER,
                    }}
                />
            );
        } else if (cellRenderer) {
            return cellRenderer({
                ...rest,
                parent: this,
                rowIndex: rowIndex + fixedRowCount,
            });
        } else {
            return null;
        }
    };

    private _cellRendererBottomRightGrid = ({columnIndex, rowIndex, ...rest}: MultiGridCellProps) => {
        const {cellRenderer, fixedColumnCount, fixedRowCount} = this.props;
        if (!cellRenderer) {
            return null;
        }
        return cellRenderer({
            ...rest,
            columnIndex: columnIndex + fixedColumnCount,
            parent: this,
            rowIndex: rowIndex + fixedRowCount,
        });
    };

    private _cellRendererTopRightGrid = ({columnIndex, ...rest}: MultiGridCellProps) => {
        const {cellRenderer, columnCount = 0, fixedColumnCount = 0} = this.props;

        if (columnIndex === columnCount - fixedColumnCount) {
            return (
                <div
                    key={rest.key}
                    style={{
                        ...rest.style,
                        width: SCROLLBAR_SIZE_BUFFER,
                    }}
                />
            );
        } else if (cellRenderer) {
            return cellRenderer({
                ...rest,
                columnIndex: columnIndex + fixedColumnCount,
                parent: this,
            });
        } else {
            return null;
        }
    };

    private _columnWidthRightGrid = ({index}) => {
        const {columnCount = 0, fixedColumnCount = 0, columnWidth = 0} = this.props;
        const {scrollbarSize, showHorizontalScrollbar} = this.state;

        // An extra cell is added to the count
        // This gives the smaller Grid extra room for offset,
        // In case the main (bottom right) Grid has a scrollbar
        // If no scrollbar, the extra space is overflow:hidden anyway
        if (showHorizontalScrollbar && index === columnCount - fixedColumnCount) {
            return scrollbarSize;
        }

        return typeof columnWidth === 'function' ? columnWidth({index: index + fixedColumnCount}) : columnWidth;
    };

    private _getBottomGridHeight(props) {
        const {height} = props;

        const topGridHeight = this._getTopGridHeight(props);

        return height - topGridHeight;
    }

    private _getLeftGridWidth(props) {
        const {fixedColumnCount, columnWidth} = props;

        if (this._leftGridWidth === undefined) {
            if (typeof columnWidth === 'function') {
                let leftGridWidth = 0;

                for (let index = 0; index < fixedColumnCount; index++) {
                    leftGridWidth += columnWidth({index});
                }

                this._leftGridWidth = leftGridWidth;
            } else {
                this._leftGridWidth = columnWidth * fixedColumnCount;
            }
        }

        return this._leftGridWidth;
    }

    private _getRightGridWidth(props) {
        const {width} = props;

        const leftGridWidth = this._getLeftGridWidth(props);

        return width - leftGridWidth;
    }

    private _getTopGridHeight(props) {
        const {fixedRowCount, rowHeight} = props;

        if (this._topGridHeight === undefined) {
            if (typeof rowHeight === 'function') {
                let topGridHeight = 0;

                for (let index = 0; index < fixedRowCount; index++) {
                    topGridHeight += rowHeight({index});
                }

                this._topGridHeight = topGridHeight;
            } else {
                this._topGridHeight = rowHeight * fixedRowCount;
            }
        }

        return this._topGridHeight;
    }

    private _handleInvalidatedGridSize() {
        if (typeof this._deferredInvalidateColumnIndex === 'number') {
            const columnIndex = this._deferredInvalidateColumnIndex;
            const rowIndex = this._deferredInvalidateRowIndex;

            this._deferredInvalidateColumnIndex = undefined;
            this._deferredInvalidateRowIndex = undefined;

            this.recomputeGridSize({
                columnIndex,
                rowIndex,
            });
            this.forceUpdate();
        }
    }

    /**
     * Avoid recreating inline styles each render; this bypasses Grid's shallowCompare.
     * This method recalculates styles only when specific props change.
     */
    // tslint:disable-next-line:cyclomatic-complexity
    private _maybeCalculateCachedStyles(resetAll) {
        const {
            columnWidth,
            enableFixedColumnScroll,
            enableFixedRowScroll,
            height = 0,
            fixedColumnCount,
            fixedRowCount,
            rowHeight,
            style,
            styleBottomLeftGrid,
            styleBottomRightGrid,
            styleTopLeftGrid,
            styleTopRightGrid,
            width,
        } = this.props;

        const sizeChange = resetAll || height !== this._lastRenderedHeight || width !== this._lastRenderedWidth;
        const leftSizeChange = resetAll || columnWidth !== this._lastRenderedColumnWidth || fixedColumnCount !== this._lastRenderedFixedColumnCount;
        const topSizeChange = resetAll || fixedRowCount !== this._lastRenderedFixedRowCount || rowHeight !== this._lastRenderedRowHeight;

        if (resetAll || sizeChange || style !== this._lastRenderedStyle) {
            this._containerOuterStyle = {
                height,
                overflow: 'visible', // Let :focus outline show through
                width,
                ...style,
            };
        }

        if (resetAll || sizeChange || topSizeChange) {
            this._containerTopStyle = {
                height: this._getTopGridHeight(this.props),
                position: 'relative',
                width,
            };

            this._containerBottomStyle = {
                height: height - this._getTopGridHeight(this.props),
                overflow: 'visible', // Let :focus outline show through
                position: 'relative',
                width,
            };
        }

        if (resetAll || styleBottomLeftGrid !== this._lastRenderedStyleBottomLeftGrid) {
            this._bottomLeftGridStyle = {
                left: 0,
                overflowX: 'hidden',
                overflowY: enableFixedColumnScroll ? 'auto' : 'hidden',
                position: 'absolute',
                ...styleBottomLeftGrid,
            };
        }

        if (resetAll || leftSizeChange || styleBottomRightGrid !== this._lastRenderedStyleBottomRightGrid) {
            this._bottomRightGridStyle = {
                left: this._getLeftGridWidth(this.props),
                position: 'absolute',
                ...styleBottomRightGrid,
            };
        }

        if (resetAll || styleTopLeftGrid !== this._lastRenderedStyleTopLeftGrid) {
            this._topLeftGridStyle = {
                left: 0,
                overflowX: 'hidden',
                overflowY: 'hidden',
                position: 'absolute',
                top: 0,
                ...styleTopLeftGrid,
            };
        }

        if (resetAll || leftSizeChange || styleTopRightGrid !== this._lastRenderedStyleTopRightGrid) {
            this._topRightGridStyle = {
                left: this._getLeftGridWidth(this.props),
                overflowX: enableFixedRowScroll ? 'auto' : 'hidden',
                overflowY: 'hidden',
                position: 'absolute',
                top: 0,
                ...styleTopRightGrid,
            };
        }

        this._lastRenderedColumnWidth = typeof columnWidth === 'function' ? 0 : columnWidth;
        this._lastRenderedRowHeight = typeof rowHeight === 'function' ? 0 : rowHeight;
        this._lastRenderedFixedColumnCount = fixedColumnCount;
        this._lastRenderedFixedRowCount = fixedRowCount;
        this._lastRenderedWidth = width;
        this._lastRenderedHeight = height;
        this._lastRenderedStyle = style;
        this._lastRenderedStyleBottomLeftGrid = styleBottomLeftGrid;
        this._lastRenderedStyleBottomRightGrid = styleBottomRightGrid;
        this._lastRenderedStyleTopLeftGrid = styleTopLeftGrid;
        this._lastRenderedStyleTopRightGrid = styleTopRightGrid;
    }

    private _prepareForRender() {
        // The typecheck was added while converting to TypeScript.
        // I don't think this works if columnWidth() or rowHeight() return changing values, but we don't need that now
        const columnWidth = typeof this.props.columnWidth === 'function' ? 0 : this.props.columnWidth;
        const rowHeight = typeof this.props.rowHeight === 'function' ? 0 : this.props.rowHeight;

        if (this._lastRenderedColumnWidth !== columnWidth || this._lastRenderedFixedColumnCount !== this.props.fixedColumnCount) {
            this._leftGridWidth = undefined;
        }
        if (this._lastRenderedRowHeight !== rowHeight || this._lastRenderedFixedRowCount !== this.props.fixedRowCount) {
            this._topGridHeight = undefined;
        }

        this._maybeCalculateCachedStyles(false);

        this._lastRenderedColumnWidth = columnWidth;
        this._lastRenderedRowHeight = rowHeight;
        this._lastRenderedFixedRowCount = this.props.fixedRowCount;
        this._lastRenderedFixedColumnCount = this.props.fixedColumnCount;
    }

    private _onScroll = (scrollInfo) => {
        const {scrollLeft, scrollTop} = scrollInfo;
        this.setState({
            scrollLeft,
            scrollTop,
        });
        const onScroll = this.props.onScroll;
        if (onScroll) {
            onScroll(scrollInfo);
        }
    };

    private _onScrollbarPresenceChange = ({horizontal, size, vertical}) => {
        const {showHorizontalScrollbar, showVerticalScrollbar} = this.state;

        if (horizontal !== showHorizontalScrollbar || vertical !== showVerticalScrollbar) {
            this.setState({
                scrollbarSize: size,
                showHorizontalScrollbar: horizontal,
                showVerticalScrollbar: vertical,
            });

            const {onScrollbarPresenceChange} = this.props;
            if (typeof onScrollbarPresenceChange === 'function') {
                onScrollbarPresenceChange({
                    horizontal,
                    size,
                    vertical,
                });
            }
        }
    };

    private _onScrollLeft = (scrollInfo) => {
        const {scrollLeft} = scrollInfo;
        /*
        Under irregular circumstances, there is a bug inside the Grid component, where the scrollLeft state is set 0, contrary
        to its props received from the MultiGrid. To counter this, this check with the scrollLeft state of the MultiGrid was added. The scrollLeft attribute
        from the Grid needs to be equal to that of the MultiGrid component. If they diverge, the bug occurred and the state
        of the Grid component needs to be realigned to the state of the MultiGrid component. This is done via re-render.
        To cause that, the scrollLeft is changed slightly, enough to cause the re-render, but to small to be visible to the user.
         */
        if (scrollLeft !== this.state.scrollLeft) {
            this._onScroll({
                scrollLeft: this.state.scrollLeft + 0.1,
                scrollTop: this.state.scrollTop,
            });
        }
    };

    private _onScrollTop = (scrollInfo) => {
        const {scrollTop} = scrollInfo;
        this._onScroll({
            scrollTop,
            scrollLeft: this.state.scrollLeft,
        });
    };

    private _renderBottomLeftGrid(props) {
        const {enableFixedColumnScroll, fixedColumnCount, fixedRowCount, rowCount, hideBottomLeftGridScrollbar} = props;
        const {showVerticalScrollbar} = this.state;

        if (!fixedColumnCount) {
            return null;
        }

        const additionalRowCount = showVerticalScrollbar ? 1 : 0,
            height = this._getBottomGridHeight(props),
            width = this._getLeftGridWidth(props),
            scrollbarSize = this.state.showVerticalScrollbar ? this.state.scrollbarSize : 0,
            gridWidth = hideBottomLeftGridScrollbar ? width + scrollbarSize : width;

        const bottomLeftGrid = (
            <Grid
                {...props}
                cellRenderer={this._cellRendererBottomLeftGrid}
                className={this.props.classNameBottomLeftGrid}
                columnCount={fixedColumnCount}
                deferredMeasurementCache={this._deferredMeasurementCacheBottomLeftGrid}
                height={height}
                onScroll={enableFixedColumnScroll ? this._onScrollTop : undefined}
                ref={this._bottomLeftGridRef}
                rowCount={Math.max(0, rowCount - fixedRowCount) + additionalRowCount}
                rowHeight={this._rowHeightBottomGrid}
                style={this._bottomLeftGridStyle}
                tabIndex={null}
                width={gridWidth}
                cellRangeRenderer={this.props.cellRangeRenderer}
            />
        );

        if (hideBottomLeftGridScrollbar) {
            return (
                <div
                    className="BottomLeftGrid_ScrollWrapper"
                    style={{
                        ...this._bottomLeftGridStyle,
                        height,
                        width,
                        overflowY: 'hidden',
                    }}>
                    {bottomLeftGrid}
                </div>
            );
        }
        return bottomLeftGrid;
    }

    private _renderBottomRightGrid(props) {
        const {columnCount, fixedColumnCount, fixedRowCount, rowCount, scrollToColumn, scrollToRow} = props;

        return (
            <Grid
                {...props}
                cellRenderer={this._cellRendererBottomRightGrid}
                className={this.props.classNameBottomRightGrid}
                columnCount={Math.max(0, columnCount - fixedColumnCount)}
                columnWidth={this._columnWidthRightGrid}
                deferredMeasurementCache={this._deferredMeasurementCacheBottomRightGrid}
                height={this._getBottomGridHeight(props)}
                onScroll={this._onScroll}
                onScrollbarPresenceChange={this._onScrollbarPresenceChange}
                ref={this._bottomRightGridRef}
                rowCount={Math.max(0, rowCount - fixedRowCount)}
                rowHeight={this._rowHeightBottomGrid}
                scrollToColumn={scrollToColumn - fixedColumnCount}
                scrollToRow={scrollToRow - fixedRowCount}
                style={this._bottomRightGridStyle}
                width={this._getRightGridWidth(props)}
                cellRangeRenderer={this.props.cellRangeRenderer}
            />
        );
    }

    private _renderTopLeftGrid(props) {
        const {fixedColumnCount, fixedRowCount} = props;

        if (!fixedColumnCount || !fixedRowCount) {
            return null;
        }

        return (
            <Grid
                {...props}
                className={this.props.classNameTopLeftGrid}
                columnCount={fixedColumnCount}
                height={this._getTopGridHeight(props)}
                ref={this._topLeftGridRef}
                rowCount={fixedRowCount}
                style={this._topLeftGridStyle}
                tabIndex={null}
                width={this._getLeftGridWidth(props)}
                cellRangeRenderer={this.props.cellRangeRenderer}
            />
        );
    }

    private _renderTopRightGrid(props) {
        const {columnCount, fixedColumnCount, fixedRowCount, scrollLeft, hideTopRightGridScrollbar} = props;
        const {showHorizontalScrollbar, scrollbarSize} = this.state;

        if (!fixedRowCount) {
            return null;
        }

        const additionalColumnCount = showHorizontalScrollbar ? 1 : 0,
            height = this._getTopGridHeight(props),
            width = this._getRightGridWidth(props),
            additionalHeight = showHorizontalScrollbar ? scrollbarSize : 0;

        let gridHeight = height,
            style = this._topRightGridStyle;

        if (hideTopRightGridScrollbar) {
            gridHeight = height + additionalHeight;
            style = {
                ...this._topRightGridStyle,
                left: 0,
            };
        }

        const topRightGrid = (
            <Grid
                {...props}
                cellRenderer={this._cellRendererTopRightGrid}
                className={this.props.classNameTopRightGrid}
                columnCount={Math.max(0, columnCount - fixedColumnCount) + additionalColumnCount}
                columnWidth={this._columnWidthRightGrid}
                deferredMeasurementCache={this._deferredMeasurementCacheTopRightGrid}
                height={gridHeight}
                onScroll={this._onScrollLeft}
                ref={this._topRightGridRef}
                rowCount={fixedRowCount}
                scrollLeft={scrollLeft}
                style={style}
                tabIndex={null}
                width={width}
                cellRangeRenderer={this.props.cellRangeRenderer}
            />
        );

        if (hideTopRightGridScrollbar) {
            return (
                <div
                    className="TopRightGrid_ScrollWrapper"
                    style={{
                        ...this._topRightGridStyle,
                        height,
                        width,
                        overflowX: 'hidden',
                    }}>
                    {topRightGrid}
                </div>
            );
        }
        return topRightGrid;
    }

    private _rowHeightBottomGrid = ({index}) => {
        const {fixedRowCount = 0, rowCount = 0, rowHeight = 0} = this.props;
        const {scrollbarSize, showVerticalScrollbar} = this.state;

        // An extra cell is added to the count
        // This gives the smaller Grid extra room for offset,
        // In case the main (bottom right) Grid has a scrollbar
        // If no scrollbar, the extra space is overflow:hidden anyway
        if (showVerticalScrollbar && index === rowCount - fixedRowCount) {
            return scrollbarSize;
        }

        return typeof rowHeight === 'function' ? rowHeight({index: index + fixedRowCount}) : rowHeight;
    };

    private _topLeftGridRef = (ref) => {
        this._topLeftGrid = ref;
    };

    private _topRightGridRef = (ref) => {
        this._topRightGrid = ref;
    };
}

polyfill(MultiGrid);

export default MultiGrid;



// WEBPACK FOOTER //
// ./src/views/Virtualized/MultiGrid/MultiGrid.tsx